import React, { useState } from "react";
import { Box, Button, Chip } from "@mui/material";
import { Code } from "@mui/icons-material";
import { MAX_EMAIL_MERGE_PARAMETERS } from "../../utils/emailMergeVariables";
import EmailMergeParameterNameDialog from "./EmailMergeParameterNameDialog";

/**
 * Insert merge parameters with a name prompt (max 4 unique).
 * Optional remove chips strip every occurrence of a used parameter from the editor.
 */
const EmailMergeVariablesBar = ({
    variables = [],
    onInsertVariable,
    onPrepareInsert,
    onRemoveVariable,
}) => {
    const usedCount = variables.length;
    const isFull = usedCount >= MAX_EMAIL_MERGE_PARAMETERS;
    const canRemove = typeof onRemoveVariable === "function";
    const [insertDialogOpen, setInsertDialogOpen] = useState(false);

    const handleInsertMouseDown = (event) => {
        event.preventDefault();
        if (isFull) return;
        // Capture caret before the dialog steals focus from the editor.
        onPrepareInsert?.();
    };

    const handleInsertClick = () => {
        if (isFull) return;
        setInsertDialogOpen(true);
    };

    const handleInsertConfirm = (name) => {
        setInsertDialogOpen(false);
        onInsertVariable?.(name);
    };

    if (typeof onInsertVariable !== "function") {
        return null;
    }

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    justifyContent: "flex-end",
                    ml: "auto",
                    minWidth: 0,
                }}
            >
                {canRemove &&
                    variables.map((name) => (
                        <Chip
                            key={name}
                            size="small"
                            label={`{{${name}}}`}
                            onDelete={() => onRemoveVariable(name)}
                            onMouseDown={(event) => event.preventDefault()}
                            sx={{
                                fontFamily: "Consolas, monospace",
                                fontWeight: 600,
                                bgcolor: "#e3f2fd",
                                border: "1px solid #90caf9",
                                "& .MuiChip-deleteIcon": {
                                    color: "#d32f2f",
                                    fontSize: 16,
                                    "&:hover": { color: "#b71c1c" },
                                },
                            }}
                        />
                    ))}
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Code sx={{ fontSize: 16 }} />}
                    onMouseDown={handleInsertMouseDown}
                    onClick={handleInsertClick}
                    disabled={isFull}
                    sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        borderRadius: 2,
                        flexShrink: 0,
                    }}
                >
                    Insert parameter ({usedCount}/{MAX_EMAIL_MERGE_PARAMETERS})
                </Button>
            </Box>

            <EmailMergeParameterNameDialog
                open={insertDialogOpen}
                mode="insert"
                usedNames={variables}
                onClose={() => setInsertDialogOpen(false)}
                onConfirm={handleInsertConfirm}
            />
        </>
    );
};

export default EmailMergeVariablesBar;
